import { useEffect, useRef, useState } from "react";

type UseVoiceInputOptions = {
  language?: string;
  timeoutMs?: number;
};

type UseVoiceInputResult = {
  isListening: boolean;
  result: string;
  interimText: string;
  error: string | null;
  startListening: () => void;
  stopListening: () => void;
};

export default function useVoiceInput(
  options: UseVoiceInputOptions = { language: "ja-JP", timeoutMs: 10000 }
): UseVoiceInputResult {
  const [result, setResult] = useState("");
  const [interimText, setInterimText] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<any>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopListening = () => {
    recognitionRef.current?.stop();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsListening(false);
  };

  const startListening = () => {
    setResult("");
    setInterimText("");
    setError(null);
    setIsListening(true);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(stopListening, options.timeoutMs);

    recognitionRef.current?.start();
  };

  useEffect(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setError("このブラウザは音声認識に対応していません");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = options.language ?? "ja-JP";
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      let finalText = "";
      let interim = "";

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += transcript;
        else interim += transcript;
      }

      if (finalText) setResult((prev) => prev + finalText);
      setInterimText(interim);
    };

    recognition.onerror = (event: any) => {
      setError(event.error ?? "音声認識エラーが発生しました");
      stopListening();
    };

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => stopListening();

    recognitionRef.current = recognition;

    return () => {
      recognitionRef.current?.abort?.();
    };
  }, []);

  return { isListening, result, interimText, error, startListening, stopListening };
}
